import * as Sentry from '@sentry/vue';
import { watch } from 'vue';
import type { Ref } from 'vue';
import { formatDate, formatTime } from '@/utils/date';

// How long everyday work takes, for the UX measurements: from the moment someone starts a task until they click Save
// (so the time the server takes is left out), sent as a Sentry transaction (op ui.task) once the save succeeded.
// Work that is abandoned or cancelled is never sent. Tags hold field names, counts and fixed categories (a new status,
// default visibility or not), never typed text or anything that points to a person or a lead.
// Times come from the wall clock: performance.now(), which Sentry uses by default, stops while a laptop sleeps, so
// long-open tabs would date the measurements in the past. A long interruption (lunch, lid closed) shows as a long
// duration, which the analysis leaves out.
// A measurement must never break the work it measures, so nothing here throws.
export type TaskTimingName = 'task.create' | 'note.create' | 'lead.move';

// A timing captured at the Save click, in ms on the wall clock
export interface StoppedTiming {
    name: TaskTimingName,
    startedAt: number,
    endedAt: number,
    tags: Record<string, string>,
}

export interface TaskTiming {
    stop: (tags?: Record<string, string>) => StoppedTiming | undefined;
}

interface TimedTransaction {
    setTag: (key: string, value: string) => void;
    finish: (endTimestamp?: number) => void;
}

type StartTransaction = (context: { name: string, op: string, startTimestamp: number }) => TimedTransaction | undefined;

const safely = <T>(measure: () => T): T | undefined => {
    try {
        return measure();
    } catch {
        return undefined;
    }
};

export const startTaskTiming = (name: TaskTimingName, { now = Date.now }: { now?: () => number } = {}): TaskTiming => {
    const startedAt = safely(now);

    return {
        stop: (tags = {}) => safely(() => {
            const endedAt = now();

            // A wall clock that stepped back (an NTP correction) would give a negative time
            return startedAt === undefined || endedAt < startedAt ? undefined : { name, startedAt, endedAt, tags };
        }),
    };
};

// Without a DSN Sentry isn't initialised; calling it then only logs a warning
const sentryTransaction: StartTransaction = (context) => (
    Sentry.getCurrentHub().getClient() ? Sentry.startTransaction(context) : undefined
);

// Call once the save has succeeded
export const sendTaskTiming = (
    stopped: StoppedTiming | undefined,
    { start = sentryTransaction }: { start?: StartTransaction } = {},
) => {
    safely(() => {
        if (!stopped) {
            return;
        }

        const transaction = start({ name: stopped.name, op: 'ui.task', startTimestamp: stopped.startedAt / 1000 });
        Object.entries(stopped.tags).forEach(([key, value]) => transaction?.setTag(key, value));
        transaction?.finish(stopped.endedAt / 1000);
    });
};

// The rich-text editors hold markup such as '<p><br></p>' when nothing is typed
export const isEmptyRichText = (value: string | null | undefined) => (
    (value ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === ''
);

export const changedFields = (before: Record<string, string>, after: Record<string, string>): string[] => (
    Object.keys(after).filter((field) => before[field] !== after[field])
);

interface TaskForm {
    isTask: boolean,
    informAssignedTo: boolean,
    type: { value: { value: string } | null },
    responsible: { id: { value: string } }[],
    startDateChosen: Date | string,
    endDateChosen?: Date | string,
    comment: string,
}

const minutesBetween = (from: Date | string, to: Date | string) => (
    Math.round((new Date(to).getTime() - new Date(from).getTime()) / 60000)
);

// The task form reduced to comparable values. The values stay in the browser; only the names of the fields that
// differ from the form's defaults are sent.
export const taskFormSnapshot = (form: TaskForm): Record<string, string> => ({
    kind: form.isTask ? 'task' : 'appointment',
    type: form.type.value?.value ?? '',
    assignee: form.responsible.map((user) => user.id.value).sort().join(','),
    date: formatDate(form.startDateChosen),
    time: formatTime(form.startDateChosen),
    // Appointments only; the end moves along with the start, so the length is what people choose
    length: form.isTask || !form.endDateChosen ? '' : String(minutesBetween(form.startDateChosen, form.endDateChosen)),
    comment: isEmptyRichText(form.comment) ? 'empty' : 'filled',
    inform: String(form.informAssignedTo),
});

// Creating a task or appointment: from the form holding its defaults to Save, tagged with the defaults people changed.
// The kind is not counted as a changed default: kind_switched says whether the in-form toggle changed it.
export interface TaskCreation {
    timing: TaskTiming,
    before: Record<string, string>,
}

export const startTaskCreation = (form: TaskForm, options?: { now?: () => number }): TaskCreation | undefined => (
    safely(() => ({ before: taskFormSnapshot(form), timing: startTaskTiming('task.create', options) }))
);

export const stopTaskCreation = (creation: TaskCreation | undefined, form: TaskForm) => safely(() => {
    if (!creation) {
        return undefined;
    }

    const after = taskFormSnapshot(form);
    const changed = changedFields(creation.before, after).filter((field) => field !== 'kind');

    return creation.timing.stop({
        kind: after.kind,
        kind_switched: String(creation.before.kind !== after.kind),
        changed: changed.join(',') || 'none',
        fields_changed: String(changed.length),
    });
});

// Logging a note: from its first character to Save. Emptying the note drops the timing, and so does Save, so the next
// note starts afresh, even while this one is still being saved.
export const useNoteTiming = (
    note: Ref<string | null>,
    isDefaultVisibility: () => boolean,
    options?: { now?: () => number },
) => {
    let timing: TaskTiming | undefined;

    watch(note, (value) => {
        if (isEmptyRichText(value)) {
            timing = undefined;
        } else if (!timing) {
            timing = startTaskTiming('note.create', options);
        }
    });

    return {
        stop: () => {
            const stopped = timing?.stop({ visibility_default: String(safely(isDefaultVisibility)) });
            timing = undefined;
            return stopped;
        },
    };
};

// Moving a lead: status and phase are separate controls with their own Save and Cancel, so each has its own timing,
// from the user's first edit that leaves a change to Save. The component reports edits from its inputs, with whether a
// change is left, because its dirty flags also flip when the lead reloads.
type LeadPart = 'status' | 'phase';

export const useLeadMoveTiming = (options?: { now?: () => number }) => {
    const timings: Partial<Record<LeadPart, TaskTiming>> = {};

    return {
        edited: (part: LeadPart, changeLeft: boolean) => {
            if (!changeLeft) {
                delete timings[part];
            } else if (!timings[part]) {
                timings[part] = startTaskTiming('lead.move', options);
            }
        },
        stop: (part: LeadPart, status?: string) => {
            const stopped = timings[part]?.stop({ saved: part, ...(status ? { status_to: status } : {}) });
            delete timings[part];
            return stopped;
        },
        // Without a part: both, as when the lead reloads and pending changes are reset
        cancelled: (part?: LeadPart) => {
            (part ? [part] : ['status', 'phase'] as LeadPart[]).forEach((key) => delete timings[key]);
        },
    };
};
